import React, { useState } from "react";
import { Button } from "./Button";
import { Wand2, Loader2, RefreshCw } from "lucide-react";
import { GlobalService } from "../../modules";
import { useAppDispatch } from "../../redux";
import { generateAIBio } from "../../redux/Actions/ClientActions";
import { useTranslation } from "react-i18next";

interface AIBioGeneratorProps {
  userId: string;
  language?: string;
  onBioGenerated: (bio: string) => void;
  disabled?: boolean;
  className?: string;
}

const AIBioGenerator: React.FC<AIBioGeneratorProps> = ({
  userId,
  language,
  onBioGenerated,
  disabled = false,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const dispatch = useAppDispatch();

  const handleGenerateBio = async () => {
    if (!userId) {
      GlobalService.showToast("warning", t("aiBioGenerator.userIdRequired"), t("aiBioGenerator.userIdRequiredMessage"));
      return;
    }

    setIsGenerating(true);
    setHasGenerated(false);

    try {
      const response = await dispatch(generateAIBio({ userId, language }));

      if (generateAIBio.fulfilled.match(response)) {
        const bio = response.payload?.bio || response.payload?.data?.bio;
        
        if (bio) {
          onBioGenerated(bio);
          setHasGenerated(true);
          GlobalService.showToast("success", t("aiBioGenerator.bioGenerated"), t("aiBioGenerator.bioGeneratedMessage"));
        } else {
          throw new Error("No bio returned from API");
        }
      } else {
        throw new Error(response.payload as string || "Failed to generate bio");
      }
    } catch (error) {
      console.error("Error generating bio:", error);
      GlobalService.showToast("error", t("aiBioGenerator.generationFailed"), error instanceof Error ? error.message : t("aiBioGenerator.generationFailedMessage"));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        type="button"
        onClick={handleGenerateBio}
        disabled={disabled || isGenerating}
        variant={hasGenerated ? "outline" : "default"}
        className="flex items-center gap-2"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("aiBioGenerator.generating")}</span>
          </>
        ) : hasGenerated ? (
          <>
            <RefreshCw className="h-4 w-4" />
            <span>{t("aiBioGenerator.regenerate")}</span>
          </>
        ) : (
          <>
            <Wand2 className="h-4 w-4" />
            <span>{t("aiBioGenerator.generateWithAI")}</span>
          </>
        )}
      </Button>
      
      {hasGenerated && (
        <span className="text-sm text-green-600 font-medium">
          ✓ {t("aiBioGenerator.aiGenerated")}
        </span>
      )}
    </div>
  );
};

export default AIBioGenerator;

